import { defineStore } from "pinia";
import { ref, watch, toRef, computed, nextTick } from "vue";
import { i18n } from "@/locales";
import { useSnackStore } from "@/stores/snackStore";
import { useAuthStore } from "@/stores/authStore";
import type { Ref } from "vue";
import type { ChatTab } from "@/api/models/message/message";
import { EPracticeStaffSettingsKey } from "@/api/models/practice/practiceStaffSettings";
import type { TPracticeStaffUserSettings } from "@/api/models/practice/practiceStaffSettings";
import type { EPatientTabs } from "@/types/filter/tableFilters.types";
import { DEFAULT_DASHBOARD_TAB } from "@/composables/models/patient/useDashboardDestinations";
import { useDefaultPhonePrefix } from "@/composables/shared/useDefaultPhonePrefix";
// import { useEnvironmentStore } from "./environmentStore";

export enum NameFormatSetting {
  LAST_FIRST = "LAST_FIRST",
  FIRST_LAST = "FIRST_LAST",
  FIRST_MIDDLE_LAST = "FIRST_MIDDLE_LAST",
}

export enum Language {
  English = "en",
}

export const inputFormats = [
  "MM/DD/YYYY", // US
  "DD/MM/YYYY", // Europe, Latin America
  "YYYY-MM-DD", // ISO 8601
  "DD.MM.YYYY", // Germany, Russia
  "YYYY/MM/DD", // China, Japan
];

export const displayFormats = [
  ...inputFormats,
  "MMMM D, YYYY", // November 5, 2025
  "D MMMM YYYY", // 5 November 2025
  "MMM D, YYYY", // Nov 5, 2025
  "D MMM YYYY", // 5 Nov 2025
  "M/D/YYYY", // 11/5/2025
  "D/M/YYYY", // 5/11/2025
  "YYYY-M-D", // 2025-11-5
];

export const displayHourFormats = [
  "HH:mm", // 14:30
  "H:mm", // 14:30
  "h:mm A", // 2:30 PM
  "h:mm a", // 2:30 pm
  "hh:mm A", // 02:30 PM
  "hh:mm a", // 02:30 pm
];

export enum EEnterBehavior {
  SEND = "send",
  NEWLINE = "newline",
}

export interface AudioDevice {
  deviceId: string;
  groupId: string;
  kind: string;
  label: string;
}

const askAudioPermissions = async (): Promise<{ granted: boolean; denied: boolean }> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
    return { granted: true, denied: false };
  } catch (error: any) {
    logError(error, {
      context: { feature: "settings-store", step: "askAudioPermissions" },
    });
    console.warn("Permission not granted:", error);
    const isDenied = error.name === "NotAllowedError" || error.name === "PermissionDeniedError";
    return { granted: false, denied: isDenied };
  }
};

const fetchAudioDevices = async (): Promise<{
  devices: AudioDevice[];
  permissionDenied: boolean;
}> => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.error("Audio device detection is not supported in this browser");
      return { devices: [], permissionDenied: false };
    }

    const permission = await askAudioPermissions();
    if (!permission.granted) {
      return { devices: [], permissionDenied: permission.denied };
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    return {
      devices: devices.filter((device) => device.kind === "audioinput") as AudioDevice[],
      permissionDenied: false,
    };
  } catch (error) {
    logError(error, {
      context: { feature: "settings-store", step: "fetchAudioDevices" },
    });
    console.error("Audio device enumeration error:", error);
    return { devices: [], permissionDenied: false };
  }
};

type PracticeStaffPreferences = NonNullable<TPracticeStaffUserSettings["prefferences_v1"]>;

export const SETTINGS_DEFAULTS: Required<
  Omit<
    PracticeStaffPreferences,
    EPracticeStaffSettingsKey.AUDIO_DEVICE | EPracticeStaffSettingsKey.LAST_KNOWN_TZ
  >
> = {
  [EPracticeStaffSettingsKey.IS_DARK_MODE]: false,
  [EPracticeStaffSettingsKey.NAME_FORMAT_SETTING]: NameFormatSetting.FIRST_LAST,
  [EPracticeStaffSettingsKey.LANGUAGE]: Language.English,
  [EPracticeStaffSettingsKey.ADMINISTRATIVE_ASSISTANT_HINTS_DISABLED]: false,
  [EPracticeStaffSettingsKey.CHAT_ASSISTANT_HINTS_DISABLED]: false,
  [EPracticeStaffSettingsKey.SETTINGS_MENU_COLLAPSED]: false,
  [EPracticeStaffSettingsKey.CHAT_DEFAULT_TAB]: "main" as ChatTab,
  [EPracticeStaffSettingsKey.CHAT_ENTER_BEHAVIOR]: EEnterBehavior.SEND,
  [EPracticeStaffSettingsKey.CHAT_SYSTEM_MSGS]: false,
  [EPracticeStaffSettingsKey.ANNO_CONTROL]: false,
  [EPracticeStaffSettingsKey.DATE_FORMAT_INPUT]: inputFormats[0]!,
  [EPracticeStaffSettingsKey.DATE_FORMAT_DISPLAY]: displayFormats[0]!,
  [EPracticeStaffSettingsKey.HOUR_FORMAT_DISPLAY]: displayHourFormats[0]!,
  [EPracticeStaffSettingsKey.SCHEDULE_TIMELINE]: false,
  [EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED]: false,
  [EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED]: false,
  [EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED]: false,
  [EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN]: false,
  [EPracticeStaffSettingsKey.DASHBOARD_DEFAULT_TAB]: DEFAULT_DASHBOARD_TAB,
  [EPracticeStaffSettingsKey.LAST_REFERRAL_PRACTICE]: null as string | null,
  [EPracticeStaffSettingsKey.SKIP_CONFIRMATION_DIALOGS]: false,
  [EPracticeStaffSettingsKey.FORM_CUSTOMIZATION_BETA]: false,
};

export const useSettingsStore = defineStore("settingsStore", () => {
  const snackStore = useSnackStore();
  // const envStore = useEnvironmentStore();

  const { defaultPrefixResolution } = useDefaultPhonePrefix();

  const authStore = useAuthStore();
  const UP = computed<PracticeStaffPreferences>(() => {
    const userSettings = (authStore.authUser?.logged_staff?.user_settings as any) || {};
    return (userSettings.prefferences_v1 || {}) as PracticeStaffPreferences;
  });

  const userPreferences = UP.value;
  const settingsRefs = {
    [EPracticeStaffSettingsKey.IS_DARK_MODE]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.IS_DARK_MODE] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.IS_DARK_MODE]
    ),
    [EPracticeStaffSettingsKey.NAME_FORMAT_SETTING]: ref<NameFormatSetting>(
      userPreferences[EPracticeStaffSettingsKey.NAME_FORMAT_SETTING] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.NAME_FORMAT_SETTING]
    ),
    [EPracticeStaffSettingsKey.LANGUAGE]: ref<Language>(
      userPreferences[EPracticeStaffSettingsKey.LANGUAGE] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.LANGUAGE]
    ),
    [EPracticeStaffSettingsKey.ADMINISTRATIVE_ASSISTANT_HINTS_DISABLED]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.ADMINISTRATIVE_ASSISTANT_HINTS_DISABLED] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.ADMINISTRATIVE_ASSISTANT_HINTS_DISABLED]
    ),
    [EPracticeStaffSettingsKey.CHAT_ASSISTANT_HINTS_DISABLED]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.CHAT_ASSISTANT_HINTS_DISABLED] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.CHAT_ASSISTANT_HINTS_DISABLED]
    ),
    [EPracticeStaffSettingsKey.AUDIO_DEVICE]: ref<AudioDevice | null>(null),
    [EPracticeStaffSettingsKey.SETTINGS_MENU_COLLAPSED]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.SETTINGS_MENU_COLLAPSED] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.SETTINGS_MENU_COLLAPSED]
    ),
    [EPracticeStaffSettingsKey.CHAT_DEFAULT_TAB]: ref<ChatTab>(
      userPreferences[EPracticeStaffSettingsKey.CHAT_DEFAULT_TAB] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.CHAT_DEFAULT_TAB]
    ),
    [EPracticeStaffSettingsKey.CHAT_ENTER_BEHAVIOR]: ref<EEnterBehavior>(
      userPreferences[EPracticeStaffSettingsKey.CHAT_ENTER_BEHAVIOR] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.CHAT_ENTER_BEHAVIOR]
    ),
    [EPracticeStaffSettingsKey.CHAT_SYSTEM_MSGS]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.CHAT_SYSTEM_MSGS] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.CHAT_SYSTEM_MSGS]
    ),
    [EPracticeStaffSettingsKey.ANNO_CONTROL]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.ANNO_CONTROL] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.ANNO_CONTROL]
    ),
    [EPracticeStaffSettingsKey.DATE_FORMAT_INPUT]: ref<string>(
      userPreferences[EPracticeStaffSettingsKey.DATE_FORMAT_INPUT] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.DATE_FORMAT_INPUT]
    ),
    [EPracticeStaffSettingsKey.DATE_FORMAT_DISPLAY]: ref<string>(
      userPreferences[EPracticeStaffSettingsKey.DATE_FORMAT_DISPLAY] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.DATE_FORMAT_DISPLAY]
    ),
    [EPracticeStaffSettingsKey.HOUR_FORMAT_DISPLAY]: ref<string>(
      userPreferences[EPracticeStaffSettingsKey.HOUR_FORMAT_DISPLAY] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.HOUR_FORMAT_DISPLAY]
    ),
    [EPracticeStaffSettingsKey.SCHEDULE_TIMELINE]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.SCHEDULE_TIMELINE] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.SCHEDULE_TIMELINE]
    ),
    [EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED]
    ),
    [EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED]
    ),
    [EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED]
    ),
    [EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN]
    ),
    [EPracticeStaffSettingsKey.DASHBOARD_DEFAULT_TAB]: ref<EPatientTabs | string>(
      userPreferences[EPracticeStaffSettingsKey.DASHBOARD_DEFAULT_TAB] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.DASHBOARD_DEFAULT_TAB]
    ),
    [EPracticeStaffSettingsKey.LAST_REFERRAL_PRACTICE]: ref<string | null>(
      userPreferences[EPracticeStaffSettingsKey.LAST_REFERRAL_PRACTICE] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.LAST_REFERRAL_PRACTICE]
    ),
    [EPracticeStaffSettingsKey.SKIP_CONFIRMATION_DIALOGS]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.SKIP_CONFIRMATION_DIALOGS] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.SKIP_CONFIRMATION_DIALOGS]
    ),
    [EPracticeStaffSettingsKey.FORM_CUSTOMIZATION_BETA]: ref<boolean>(
      userPreferences[EPracticeStaffSettingsKey.FORM_CUSTOMIZATION_BETA] ??
        SETTINGS_DEFAULTS[EPracticeStaffSettingsKey.FORM_CUSTOMIZATION_BETA]
    ),
  };

  const userDevices = ref<AudioDevice[]>([]);
  const isBackfillingPreferencesFromAuth = ref(false);
  const locale = toRef(i18n.global, "locale") as Ref<Language>;
  locale.value = settingsRefs[EPracticeStaffSettingsKey.LANGUAGE].value;

  const API_ONLY_KEYS: Set<EPracticeStaffSettingsKey> = new Set([
    EPracticeStaffSettingsKey.DASHBOARD_TOUR_COMPLETED,
    EPracticeStaffSettingsKey.OFFICES_CONTRACTS_TOUR_COMPLETED,
    EPracticeStaffSettingsKey.OFFICE_BILLING_TOUR_COMPLETED,
    EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN,
  ]);

  (Object.entries(settingsRefs) as [EPracticeStaffSettingsKey, Ref<unknown>][]).forEach(
    ([key, refValue]) => {
      watch(refValue, (newVal) => {
        if (key === EPracticeStaffSettingsKey.LANGUAGE) {
          locale.value = newVal as Language;
        }
        if (!API_ONLY_KEYS.has(key)) {
          update(key, newVal);
        }
      });
    }
  );

  watch(UP, (prefs) => {
    isBackfillingPreferencesFromAuth.value = true;
    try {
      for (const [key, refValue] of Object.entries(settingsRefs) as [
        keyof typeof settingsRefs,
        Ref<unknown>,
      ][]) {
        if (key === EPracticeStaffSettingsKey.AUDIO_DEVICE) continue;

        refValue.value = (prefs as any)[key] ?? SETTINGS_DEFAULTS[key];
      }
    } finally {
      void nextTick(() => {
        isBackfillingPreferencesFromAuth.value = false;
      });
    }
  });

  function get(key: EPracticeStaffSettingsKey, defaultValue?: any) {
    return localStorage.getItem(key) || defaultValue;
  }

  function update(key: EPracticeStaffSettingsKey, value: unknown) {
    const stringValue = typeof value === "string" ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
  }

  async function initializeAudioDevices() {
    try {
      const result = await fetchAudioDevices();
      userDevices.value = result.devices;

      if (result.permissionDenied) {
        console.warn("Microphone permission denied by user");
        return;
      }

      const savedDevice = get(EPracticeStaffSettingsKey.AUDIO_DEVICE);
      const defaultDevice = result.devices[0];
      const selectedDevice = savedDevice ? JSON.parse(savedDevice) : defaultDevice;

      settingsRefs[EPracticeStaffSettingsKey.AUDIO_DEVICE].value = result.devices.some(
        (device) => device.deviceId === selectedDevice?.deviceId
      )
        ? selectedDevice
        : defaultDevice;
    } catch (error) {
      console.error("Failed to initialize audio devices:", error);
      snackStore.showError("Failed to initialize audio devices");
    }
  }

  const displayDatetimeFormat = computed(() => {
    const dateFormat = settingsRefs[EPracticeStaffSettingsKey.DATE_FORMAT_DISPLAY].value;
    const hourFormat = settingsRefs[EPracticeStaffSettingsKey.HOUR_FORMAT_DISPLAY].value;
    return `${dateFormat} ${hourFormat}`;
  });

  const toggleTheme = () => {
    settingsRefs[EPracticeStaffSettingsKey.IS_DARK_MODE].value =
      !settingsRefs[EPracticeStaffSettingsKey.IS_DARK_MODE].value;
    update(
      EPracticeStaffSettingsKey.IS_DARK_MODE,
      settingsRefs[EPracticeStaffSettingsKey.IS_DARK_MODE].value
    );
  };

  return {
    ...settingsRefs,
    userDevices,
    isBackfillingPreferencesFromAuth,
    initializeAudioDevices,
    displayDatetimeFormat,
    toggleTheme,
    defaultPrefixResolution,
  };
});
